import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CreditCard, ArrowUpCircle, Loader2, LifeBuoy, Shield } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBrand } from "@/contexts/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { canPay, PAYER_ROLE_REQUIRED_MESSAGE } from "@/lib/billing-payer";
import { usePlans, Plan } from "@/hooks/usePlans";
import { resolvePaywallReason } from "@/lib/paywall-reason";
import { effectiveConversationLimit } from "../../supabase/functions/_shared/conversation-limit";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { isSelfServePlan } from "@/lib/self-serve-plans";
import { formatPlanConversations } from "@/lib/conversation-count";
import { useCreditCoverage } from "@/hooks/useCreditCoverage";
import { AGENCY_BILLING_CONTACT_LABEL } from "@/lib/agency-billing";
import { limitReachedSummary, unattendedConversations } from "@/lib/limit-screen";
import { isBillingRecoveryRoute } from "@/lib/billing-recovery-routes";
import { PlanChangeDialog } from "@/components/settings/PlanChangeDialog";
import { usePaywallPlanSubscribe } from "@/hooks/usePaywallPlanSubscribe";
import { planPaymentStrategy } from "@/lib/plan-payment-strategy";
import { customerFacingError, readFunctionError } from "@/lib/payment-error-message";

interface OrgGate {
  id: string;
  plan: string | null;
  ai_active: boolean;
  manual_block: boolean;
  conversations_used: number;
  conversations_limit: number;
  conversations_extra: number | null;
  limit_exceeded_at: string | null;
  per_use_authorization_lost_at: string | null;
}

interface Props {
  organizationId: string | null;
  /** Super admins see the same paywall but can dismiss it for the session. */
  isSuperAdmin: boolean;
  agencyManaged?: boolean;
}

function overageBody(org: OrgGate): string {
  const unattended = unattendedConversations(org.conversations_used, effectiveConversationLimit(org));
  return `${limitReachedSummary(unattended, org.limit_exceeded_at)}. La IA y automatizaciones están pausadas.`;
}

const bypassKey = (orgId: string) => `paywall-bypass:${orgId}`;

const ACCOUNT_PAYMENTS_PATH = "/app/settings/account#pagos";

interface RecoveryBannerProps {
  title: string;
  body: string;
}

function PaywallRecoveryBanner({ title, body }: RecoveryBannerProps) {
  return (
    <div
      role="status"
      data-testid="paywall-recovery-banner"
      className="order-first shadow-panel mx-2 mt-2 md:mx-3 md:mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-start gap-2 text-sm"
    >
      <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-destructive min-w-0">
        <span className="font-semibold">{title}.</span> {body}
      </p>
    </div>
  );
}

/**
 * Paywall overlay rendered when the workspace is suspended because of unpaid
 * subscription (ai_active = false), a super-admin manual block, or overage past
 * the conversation limit grace.
 *
 * Super admins get the customer's exact view plus a button to step past it, so
 * a blocked account can still be inspected and unblocked from the console.
 */
export function ConversationLimitPaywall({ organizationId, isSuperAdmin, agencyManaged = false }: Props) {
  const { plans } = usePlans();
  const brand = useBrand();
  const { userRole, user, agent } = useAuth();
  const userCanPay = canPay(userRole);
  const navigate = useNavigate();
  const location = useLocation();
  const [org, setOrg] = useState<OrgGate | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [loading, setLoading] = useState<string | null>(null);
  const [bypassed, setBypassed] = useState(false);
  const planSubscribe = usePaywallPlanSubscribe<Plan>(organizationId);

  const fetchOrg = useCallback(async () => {
    if (!organizationId) {
      setOrg(null);
      setOrgLoading(false);
      return;
    }
    const { data } = await supabase
      .from("organizations")
      .select("id, plan, ai_active, manual_block, conversations_used, conversations_limit, conversations_extra, limit_exceeded_at, per_use_authorization_lost_at")
      .eq("id", organizationId)
      .maybeSingle();
    setOrg((data as OrgGate | null) ?? null);
    setOrgLoading(false);
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) {
      setBypassed(false);
      return;
    }
    try {
      setBypassed(sessionStorage.getItem(bypassKey(organizationId)) === "1");
    } catch {
      setBypassed(false);
    }
  }, [organizationId]);

  // Initial fetch + realtime subscription so the overlay appears as soon as
  // billing-charge flips ai_active and disappears as soon as the customer pays.
  useEffect(() => {
    setOrgLoading(true);
    fetchOrg();

    if (!organizationId) return;

    const channel = supabase
      .channel(`org-paywall-${organizationId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "organizations", filter: `id=eq.${organizationId}` },
        () => { fetchOrg(); },
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === "visible") fetchOrg();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [organizationId, fetchOrg]);

  const overage = !!org && resolvePaywallReason(org) === "overage";
  const { coverage: creditCoverage, loading: creditCoverageLoading } = useCreditCoverage(
    organizationId,
    overage,
    org?.conversations_used,
  );

  if (orgLoading || !org) return null;
  if (isSuperAdmin && bypassed) return null;

  const reason = resolvePaywallReason(org);
  if (!reason) return null;
  if (brand.id === "flow" && !agencyManaged && reason !== "manual-block") return null;
  if (reason === "overage" && (creditCoverageLoading || creditCoverage?.covers)) return null;

  const manualBlocked = reason === "manual-block";
  const authorizationLost = reason === "authorization-lost";
  const debtSuspended = reason === "debt" || authorizationLost;
  const overageActive = reason === "overage";

  const currentPlan = plans.find((p: Plan) => p.id === org.plan) || null;
  const nextPlan = plans
    .filter((p: Plan) => isSelfServePlan(p) && p.price_monthly > (currentPlan?.price_monthly || 0))
    .sort((a: Plan, b: Plan) => a.sort_order - b.sort_order)[0];

  const reasonHeading = manualBlocked
    ? "Servicio suspendido"
    : authorizationLost
      ? "Tus agentes de IA están en pausa"
    : debtSuspended
      ? "El servicio está pausado"
      : "Tu cuenta superó el límite";
  const reasonBody = manualBlocked
    ? "Tu cuenta fue suspendida por el equipo de soporte. Contáctanos para regularizar tu situación y reactivar el servicio."
    : authorizationLost
      ? "Cancelaste en Mercado Pago la autorización de cobro por uso, así que la IA y las automatizaciones quedaron en pausa. Pagar el saldo pendiente deja tu cuenta al día, pero tus agentes solo vuelven a responder cuando autorices de nuevo el cobro con nuestro equipo de soporte."
    : debtSuspended
      ? "Tu pago está vencido y la IA junto con las automatizaciones quedaron pausadas. Regulariza tu cuenta para reactivarlas."
      : overageBody(org);

  const handleUpgrade = async () => {
    if (!nextPlan) return;
    if (planPaymentStrategy(nextPlan) === "preapproval") {
      planSubscribe.choose(nextPlan);
      return;
    }
    setLoading("upgrade");
    try {
      const { data, error } = await supabase.functions.invoke("mercadopago-checkout", {
        body: {
          plan_id: nextPlan.id,
          organization_id: org.id,
        },
      });
      if (error) throw new Error((await readFunctionError(error)) || error.message);
      if (data?.init_point) {
        window.open(data.init_point, "_blank");
      }
    } catch (err) {
      toast.error(customerFacingError(err instanceof Error ? err.message : "", "Error al generar el checkout"));
      console.error(err);
    } finally {
      setLoading(null);
    }
  };

  const goToAccount = () => navigate(ACCOUNT_PAYMENTS_PATH);

  const handleBypass = () => {
    if (organizationId) {
      try { sessionStorage.setItem(bypassKey(organizationId), "1"); } catch { /* private mode */ }
    }
    setBypassed(true);
  };

  if (!agencyManaged && !manualBlocked && isBillingRecoveryRoute(location.pathname)) {
    const bannerTitle = overageActive
      ? limitReachedSummary(unattendedConversations(org.conversations_used, effectiveConversationLimit(org)), org.limit_exceeded_at)
      : reasonHeading;
    const bannerBody = overageActive
      ? "La IA y las automatizaciones están pausadas. Desde aquí puedes recargar saldo o cambiar de plan para reactivarlas."
      : authorizationLost
        ? `Desde aquí puedes pagar tu saldo pendiente. Para que tus agentes vuelvan a responder, autoriza de nuevo el cobro con soporte: ${brand.supportEmail}.`
        : "Tu pago está vencido. Paga la mensualidad pendiente o actualiza tu método de pago en «Pagos y saldo» para reactivar la IA y las automatizaciones.";
    return <PaywallRecoveryBanner title={bannerTitle} body={bannerBody} />;
  }

  if (agencyManaged) {
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="w-full max-w-lg mx-4">
          <Card className="border-destructive/30 shadow-lg">
            <CardContent className="pt-8 pb-8 px-8 text-center space-y-4">
              <div className="flex justify-center">
                <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-foreground">{reasonHeading}</h2>
              <p className="text-muted-foreground">
                La IA y las automatizaciones de este workspace están en pausa por el estado de su facturación.
              </p>
              <p className="text-sm font-semibold text-foreground">{AGENCY_BILLING_CONTACT_LABEL}.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-4">
        <Card className="border-destructive/30 shadow-lg">
          <CardContent className="pt-8 pb-8 px-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground">{reasonHeading}</h2>
              <p className="text-muted-foreground">{reasonBody}</p>
            </div>

            <p className="text-sm font-medium text-foreground">
              {manualBlocked ? "Para reactivar tu cuenta:" : authorizationLost ? "Para que tus agentes vuelvan a responder:" : "Para seguir operando, elige una opción:"}
            </p>

            <div className={cn("grid grid-cols-1 gap-4", !manualBlocked && "sm:grid-cols-2")}>
              {/* Manual block: cash customers have no Mercado Pago flow to run */}
              {(manualBlocked || authorizationLost) && (
                <Card className="border-2 hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6 pb-6 px-4 text-center space-y-3">
                    <LifeBuoy className="h-8 w-8 mx-auto text-primary" />
                    <h3 className="font-semibold">Contactar a soporte</h3>
                    <p className="text-sm text-muted-foreground">
                      {authorizationLost
                        ? "Escríbenos y te ayudamos a autorizar de nuevo el cobro."
                        : "Escríbenos y reactivamos tu cuenta apenas quede al día."}
                    </p>
                    <p className="text-xs text-muted-foreground break-all">{brand.supportEmail}</p>
                    <Button asChild className="w-full">
                      <a href={`mailto:${brand.supportEmail}?subject=${encodeURIComponent(authorizationLost ? "Autorizar de nuevo el cobro" : "Reactivación de cuenta")}`}>
                        Escribir a soporte
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {debtSuspended && currentPlan && (
                <Card className="border-2 hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6 pb-6 px-4 text-center space-y-3">
                    <CreditCard className="h-8 w-8 mx-auto text-primary" />
                    <h3 className="font-semibold">
                      {authorizationLost ? "Pagar saldo pendiente" : "Regularizar cuenta"}
                    </h3>
                    {authorizationLost ? (
                      <p className="text-sm text-muted-foreground">
                        Si tienes consumo pendiente, págalo desde tu cuenta. Esto no reactiva tus agentes.
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Actualiza tu método de pago o paga la mensualidad pendiente.
                      </p>
                    )}
                    <Button className="w-full" onClick={goToAccount} disabled={loading !== null}>
                      Ir a mi cuenta
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Upgrade plan. Not under a manual block: paying does not lift it,
                  so a checkout here would only take money and leave the account shut. */}
              {!manualBlocked && nextPlan && (
                <Card className="border-2 hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6 pb-6 px-4 text-center space-y-3">
                    <ArrowUpCircle className="h-8 w-8 mx-auto text-primary" />
                    <h3 className="font-semibold">Subir de plan</h3>
                    <p className="text-2xl font-bold text-primary">
                      ${nextPlan.price_monthly.toLocaleString("es-CO")} COP
                      <span className="text-sm font-normal text-muted-foreground">/mes</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Plan {nextPlan.name} — {formatPlanConversations(nextPlan, "conversaciones/mes")}
                    </p>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={handleUpgrade}
                      disabled={loading !== null || !userCanPay}
                      title={userCanPay ? undefined : PAYER_ROLE_REQUIRED_MESSAGE}
                    >
                      {loading === "upgrade" ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Cambiar plan
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {!userCanPay && (
              <p className="text-xs text-muted-foreground text-center">{PAYER_ROLE_REQUIRED_MESSAGE}</p>
            )}

            {isSuperAdmin && (
              <div className="pt-2 border-t border-border space-y-2">
                <p className="text-xs text-muted-foreground">
                  Estás viendo lo que ve el cliente. Como super admin puedes seguir operando este workspace.
                </p>
                <Button variant="ghost" size="sm" onClick={handleBypass}>
                  <Shield className="h-4 w-4 mr-2" /> Continuar como super admin
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {planSubscribe.plan && (
        <PlanChangeDialog
          open={planSubscribe.open}
          onOpenChange={planSubscribe.onOpenChange}
          currentPlan={currentPlan}
          selectedPlan={planSubscribe.plan}
          isPlanChange={!!currentPlan && currentPlan.price_monthly > 0}
          hasPreapproval={planSubscribe.hasPreapproval}
          loading={planSubscribe.loading}
          defaultPayerEmail={agent?.email || user?.email || ""}
          onConfirm={planSubscribe.confirm}
        />
      )}
    </div>
  );
}
